import React from 'react';
import { useSelector } from 'store';
import type { ViewCountCell } from 'api/unread-counts';
import {
  UNIFIED_KIND_TO_WORKFLOW_STATUS,
  type ChatSidebarScopeTab,
  type UnifiedListKind,
  type UnifiedStatusKind,
} from 'utils/chat-sidebar-unified-statuses';

const EMPTY: ViewCountCell = { total: 0, unread: 0 };

function sumScope(scopeCounts?: Record<string, ViewCountCell>): ViewCountCell {
  if (!scopeCounts) return EMPTY;
  let total = 0;
  let unread = 0;
  for (const key of Object.keys(scopeCounts)) {
    total += scopeCounts[key]?.total || 0;
    unread += scopeCounts[key]?.unread || 0;
  }
  return { total, unread };
}

/**
 * מוני תצוגת system (total + unread) לפי scope+kind, מתוך ה-slice viewCounts.
 */
export function useViewCounts(scope: ChatSidebarScopeTab, kind: UnifiedListKind): ViewCountCell {
  const scopeCounts = useSelector((s) => s.viewCounts.system?.[scope]);
  const unassignedCell = useSelector((s) => s.viewCounts.unassigned);

  return React.useMemo<ViewCountCell>(() => {
    //* «לא משויכות» = סטטוס 1 וגם בלי manager, כמו הרשימה (חקירת 4.11) — תא ייעודי מהשרת,
    //* בלי תלות ב-scope; נפילה לתא הסטטוס הישן כל עוד השרת לא שולח אותו.
    if (kind === 'unassigned' && unassignedCell) return unassignedCell;
    if (!scopeCounts) return EMPTY;
    if (kind === 'allConversations') return sumScope(scopeCounts);
    const statusId = UNIFIED_KIND_TO_WORKFLOW_STATUS[kind as UnifiedStatusKind];
    return scopeCounts[String(statusId)] ?? EMPTY;
  }, [scopeCounts, unassignedCell, kind]);
}

/** מוני תצוגה מותאמת (total + unread) לפי viewId. */
export function useCustomViewCounts(viewId: string | number): ViewCountCell {
  return useSelector((s) => s.viewCounts.custom[String(viewId)]) ?? EMPTY;
}

/** מוני תצוגת «בוטים» — שיחות שמוחזקות כרגע אצל בוט (מוחרגות מהמונים הרגילים). */
export function useBotsViewCounts(): ViewCountCell {
  return useSelector((s) => s.viewCounts.bots) ?? EMPTY;
}
